import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { companyTransactions, type TransactionItem } from '../../api/company/client'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'
import { Loader2, Search, Download, ChevronUp, ChevronDown } from 'lucide-react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import Papa from 'papaparse'
import * as XLSX from 'xlsx'

const PAGE_SIZE = 20
const SORT_OPTIONS = [
  { value: 'created_at', labelKey: 'company.transactions.sortDate' },
  { value: 'amount', labelKey: 'company.transactions.sortAmount' },
  { value: 'status', labelKey: 'company.transactions.sortStatus' },
]

const STATUS_KEYS: Record<string, string> = {
  completed: 'company.statusCompleted',
  pending: 'company.statusPending',
  failed: 'company.statusFailed',
  refunded: 'company.statusRefunded',
}

export default function CompanyTransactions() {
  const { t } = useTranslation('portal')
  const [page, setPage] = useState(0)
  const [status, setStatus] = useState('')
  const [dateFrom, setDateFrom] = useState<Date | null>(null)
  const [dateTo, setDateTo] = useState<Date | null>(null)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState('created_at')
  const [order, setOrder] = useState<'asc' | 'desc'>('desc')

  const { data, isLoading } = useQuery({
    queryKey: [
      'company',
      'transactions',
      page,
      status,
      dateFrom?.toISOString(),
      dateTo?.toISOString(),
      search,
      sort,
      order,
    ],
    queryFn: async () =>
      (
        await companyTransactions.list({
          skip: page * PAGE_SIZE,
          limit: PAGE_SIZE,
          status: status || undefined,
          date_from: dateFrom?.toISOString(),
          date_to: dateTo?.toISOString(),
          search: search || undefined,
          sort,
          order,
        })
      ).data,
  })

  const items = (data?.items ?? []) as TransactionItem[]
  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / PAGE_SIZE)

  const exportCsv = () => {
    const rows = items.map((tx) => ({
      id: tx.id,
      type: tx.is_internal_receipt ? t('company.transactions.internalReceipt') : t('company.transactions.typeFiscal'),
      fiscal_number: tx.fiscal_number,
      amount: tx.amount,
      fees: Number(tx.bank_fee) + Number(tx.platform_fee),
      net: tx.net_amount,
      currency: tx.currency,
      status: tx.status,
      created_at: tx.created_at,
    }))
    const csv = Papa.unparse(rows)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `transactions-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const exportExcel = () => {
    const rows = items.map((tx) => ({
      Type: tx.is_internal_receipt ? t('company.transactions.internalReceipt') : t('company.transactions.typeFiscal'),
      Fiscal: tx.fiscal_number,
      Amount: tx.amount,
      Fees: Number(tx.bank_fee) + Number(tx.platform_fee),
      Net: tx.net_amount,
      Currency: tx.currency,
      Status: tx.status,
      Date: tx.created_at,
    }))
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Transactions')
    XLSX.writeFile(wb, `transactions-${format(new Date(), 'yyyy-MM-dd')}.xlsx`)
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">{t('company.transactions.title')}</h1>

      <div className="rounded-xl bg-white border border-gray-200 p-4 mb-6 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[200px]">
            <label className="block text-sm text-gray-600 mb-1">{t('company.transactions.search')}</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setPage(0)
                }}
                placeholder={t('company.transactions.searchPlaceholder')}
                className="w-full rounded-lg bg-white border border-gray-300 pl-9 pr-3 py-2 text-gray-900 placeholder-gray-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">{t('company.transactions.status')}</label>
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value)
                setPage(0)
              }}
              className="rounded-lg bg-white border border-gray-300 px-3 py-2 text-gray-900"
            >
              <option value="">{t('company.transactions.all')}</option>
              <option value="completed">{t('company.statusCompleted')}</option>
              <option value="pending">{t('company.statusPending')}</option>
              <option value="failed">{t('company.statusFailed')}</option>
              <option value="refunded">{t('company.statusRefunded')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">{t('company.transactions.from')}</label>
            <DatePicker
              selected={dateFrom}
              onChange={(d: Date | null) => {
                setDateFrom(d ?? null)
                setPage(0)
              }}
              className="rounded-lg bg-white border border-gray-300 px-3 py-2 text-gray-900 w-[140px]"
              dateFormat="yyyy-MM-dd"
              placeholderText={t('company.transactions.datePlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">{t('company.transactions.to')}</label>
            <DatePicker
              selected={dateTo}
              onChange={(d: Date | null) => {
                setDateTo(d ?? null)
                setPage(0)
              }}
              className="rounded-lg bg-white border border-gray-300 px-3 py-2 text-gray-900 w-[140px]"
              dateFormat="yyyy-MM-dd"
              placeholderText={t('company.transactions.datePlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">{t('company.transactions.sort')}</label>
            <div className="flex rounded-lg bg-white border border-gray-300 overflow-hidden">
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="bg-transparent px-3 py-2 text-gray-900 border-r border-gray-300"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {t(o.labelKey)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setOrder((o) => (o === 'desc' ? 'asc' : 'desc'))}
                className="p-2 text-gray-500 hover:text-gray-900"
                title={order === 'desc' ? t('company.transactions.descending') : t('company.transactions.ascending')}
              >
                {order === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={exportCsv}
              className="rounded-lg bg-gray-100 hover:bg-gray-200 px-3 py-2 text-sm text-gray-700 flex items-center gap-2 border border-gray-300"
            >
              <Download className="w-4 h-4" /> {t('company.transactions.csv')}
            </button>
            <button
              type="button"
              onClick={exportExcel}
              className="rounded-lg bg-gray-100 hover:bg-gray-200 px-3 py-2 text-sm text-gray-700 flex items-center gap-2 border border-gray-300"
            >
              <Download className="w-4 h-4" /> {t('company.transactions.excel')}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl bg-white border border-gray-200 overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="p-8 flex items-center justify-center gap-2 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" /> {t('company.transactions.loading')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[640px]">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500 text-sm">
                  <th className="p-3">{t('company.overview.fiscalNumber')}</th>
                  <th className="p-3">{t('company.overview.date')}</th>
                  <th className="p-3">{t('company.overview.amount')}</th>
                  <th className="p-3">{t('company.transactions.fees')}</th>
                  <th className="p-3">{t('company.transactions.net')}</th>
                  <th className="p-3">{t('company.overview.status')}</th>
                  <th className="p-3 w-[1%]">{t('company.transactions.type')}</th>
                  <th className="p-3 w-[1%]"></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-gray-500 text-center">
                      {t('company.transactions.noTransactionsFound')}
                    </td>
                  </tr>
                ) : (
                  items.map((tx) => (
                    <tr key={tx.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-3 font-mono text-sm text-gray-700">{tx.fiscal_number}</td>
                      <td className="p-3 text-gray-500 text-sm">
                        {format(new Date(tx.created_at), 'PPp')}
                      </td>
                      <td className="p-3 text-gray-900">{Number(tx.amount).toFixed(2)} {tx.currency}</td>
                      <td className="p-3 text-gray-600">
                        {(Number(tx.bank_fee) + Number(tx.platform_fee)).toFixed(2)}
                      </td>
                      <td className="p-3 text-blue-600 font-medium">{Number(tx.net_amount).toFixed(2)}</td>
                      <td className="p-3">
                        <span
                          className={`rounded px-2 py-0.5 text-xs ${
                            tx.status === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : tx.status === 'failed'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {STATUS_KEYS[tx.status] ? t(STATUS_KEYS[tx.status]) : tx.status}
                        </span>
                      </td>
                      <td className="p-3">
                        {tx.is_internal_receipt ? (
                          <span className="rounded px-2 py-0.5 text-xs bg-violet-100 text-violet-700 whitespace-nowrap">
                            {t('company.transactions.internalReceipt')}
                          </span>
                        ) : null}
                      </td>
                      <td className="p-3">
                        <Link
                          to={`/company/transactions/${tx.id}`}
                          className="text-blue-600 hover:underline text-sm"
                        >
                          {t('company.transactions.details')}
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="p-3 border-t border-gray-200 flex justify-between items-center flex-wrap gap-2 bg-gray-50">
            <span className="text-gray-600 text-sm">
              {t('company.transactions.pagination', { total, page: page + 1, totalPages })}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1 rounded bg-white border border-gray-300 text-gray-700 disabled:opacity-50 hover:bg-gray-50"
              >
                {t('company.transactions.previous')}
              </button>
              <button
                type="button"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 rounded bg-white border border-gray-300 text-gray-700 disabled:opacity-50 hover:bg-gray-50"
              >
                {t('company.transactions.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
